/**
 * Input para múltiples nombres comunes de especie.
 * Muestra cada nombre como chip y permite añadir/eliminar.
 * El valor se guarda en BD como string separado por comas.
 */
import { useState, useCallback, KeyboardEvent } from 'react';
import { X, Plus } from 'lucide-react';

function parseNames(value: string): string[] {
  if (!value?.trim()) return [];
  return value.split(',').map(n => n.trim()).filter(Boolean);
}

function joinNames(names: string[]): string {
  return names.join(', ');
}

interface CommonNamesInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  id?: string;
}

export function CommonNamesInput({
  value,
  onChange,
  label = 'Nombres comunes',
  placeholder = 'Escribe un nombre y pulsa Enter o añadir',
  id,
}: CommonNamesInputProps) {
  const [inputValue, setInputValue] = useState('');
  const names = parseNames(value);

  const addName = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const current = parseNames(value);
      if (current.some(n => n.toLowerCase() === trimmed.toLowerCase())) return;
      onChange(joinNames([...current, trimmed]));
      setInputValue('');
    },
    [value, onChange]
  );

  const removeName = useCallback(
    (index: number) => {
      const current = parseNames(value);
      onChange(joinNames(current.filter((_, i) => i !== index)));
    },
    [value, onChange]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addName(inputValue);
    }
  };

  const handleAddClick = () => addName(inputValue);

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <div className="flex flex-wrap gap-2 rounded-lg border border-border bg-card p-2 min-h-[42px] focus-within:ring-1 focus-within:ring-primary/50 transition-shadow">
        {names.map((name, i) => (
          <span
            key={`${name}-${i}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary/10 text-primary text-sm"
          >
            {name}
            <button
              type="button"
              onClick={() => removeName(i)}
              className="hover:bg-primary/20 rounded p-0.5 -mr-0.5 transition-colors"
              aria-label={`Quitar ${name}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <div className="flex gap-1 flex-1 min-w-[140px]">
          <input
            id={id}
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={names.length === 0 ? placeholder : 'Añadir otro...'}
            className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
          />
          <button
            type="button"
            onClick={handleAddClick}
            disabled={!inputValue.trim()}
            className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Añadir nombre"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Puedes añadir varios nombres. Pulsa Enter, coma o el botón + para añadir.
      </p>
    </div>
  );
}
